/**
 * leistungMark (HOF-102) — the words for the ONE Leistung path.
 *
 * **What this module replaced, and why the copy could not simply be moved.** HOF-21's
 * ``valuedMark`` existed to mark a € as *„bewertet"* — a valuation shown INSTEAD of a
 * booking, inside a mode the reader had switched into. There is no mode and no booking:
 * ``leistung`` IS Menge × Preisspiegel-Monatsdurchschnittspreis for every row, always. A
 * chip saying „bewertet" on every Posten of every cycle marks nothing; it is the wallpaper
 * a farmer stops seeing, on the line where seeing matters most.
 *
 * So the marking inverts. What earns a word now is the EXCEPTION:
 *
 *   - **„nicht bewertet"** — this Posten's Menge has no price, so it contributed **0 €**.
 *     Not the booked €, which is the whole change: booked revenue reaches Leistung by no
 *     route. Every ``REFUSAL_NOTE`` string had to be rewritten for that — the old ones
 *     ended *„deshalb bleibt der gebuchte Betrag stehen"*, which is now a plain falsehood
 *     about the number beside the chip, and would have passed review as a rename.
 *   - **the Rechenweg note** — Leistung's basis said once, under the cascade, plus the two
 *     caveats that ride the fact: how many Posten went unpriced (D2) and whether the VAT
 *     basis behind the prices is established (D1 / HOF-45).
 *
 * „geschätzt" stays untouched and stays different: it means *the curated book answered
 * because the Preisspiegel could not*, and the two never co-occur — the worker emits
 * ``leistung_posten`` and ``leistung_estimate`` as alternatives.
 *
 * **The hover is the honesty.** A blend across counterparties and channels is a portfolio
 * number, not a market quote (Antonio, 2026-08-23: *"we don't need to separate channels …
 * weighted average price"*), and a reader can only judge one if they are told what it is
 * made of: the month it came from, the item it was priced as, how many observations and
 * how many buyers. Under a carry-forward the price month is NOT the Posten's month, and
 * saying so is the difference between a weaker answer and a wrong one.
 */
import './estimateMark.css';
import type { LeistungPostenBlock, LeistungPostenItem, ValuationRefusal } from './types';

/**
 * The chip word on a Posten that carries no price.
 *
 * Worth its own word rather than a missing badge: inside a panel whose every other row IS
 * priced, silence reads as „priced", and a 0,00 € contribution that reads as a price is
 * the one thing this cannot say.
 */
export const UNPRICED_LABEL = 'nicht bewertet';

/**
 * Why a Posten has no price, and — the part HOF-102 changed — what that COSTS.
 *
 * Every string ends on the same fact, because it is the same fact: the Menge is in the
 * Leistung at **0 €**. Under HOF-21 a refusal kept the booked €, so the copy could end
 * „…deshalb bleibt der gebuchte Betrag stehen" and be true. It is not true now, and a
 * module that had merely been renamed would still be saying it.
 *
 * ``not_netto`` is the one to read twice. Deckungsbeitrag ist netto (D6, 2026-08-21) and
 * an explicitly BRUTTO month is still refused — the only refusal HOF-102's W1 kept, since
 * a berry Gutschrift reading `inkl. Umsatzsteuer 10,7%` would overstate the Leistung by
 * more than twice Max's whole ±5 % referral bar, in the flattering direction. A month with
 * NO declared basis is now used and flagged (``BASIS_UNKNOWN_NOTE``) rather than refused:
 * the strict gate refused 4.124 of 4.124 prod cells and valued nothing for four releases.
 */
export const REFUSAL_NOTE: Record<ValuationRefusal, string> = {
  not_netto:
    'Nicht bewertet: für diesen Monat sind die Preise ausdrücklich als Bruttopreise erfasst. ' +
    'Der Deckungsbeitrag rechnet netto, deshalb steht diese Menge mit 0,00 € in der Leistung.',
  mixed_unit:
    'Nicht bewertet: für diesen Monat liegen Preise in zwei verschiedenen Einheiten vor. ' +
    'Ein Mischpreis daraus wäre keine gültige Zahl, deshalb steht diese Menge mit 0,00 € in der Leistung.',
  incomplete:
    'Nicht bewertet: für diesen Monat konnte ein Teil der Verkäufe nicht in den Durchschnitt ' +
    'einfließen, weil Betrag oder Menge fehlen. Ein Durchschnitt aus dem Rest wäre nicht der ' +
    'Monatspreis, deshalb steht diese Menge mit 0,00 € in der Leistung.',
  multi_item:
    'Nicht bewertet: diese Kultur wird im Preisspiegel unter mehreren Artikeln geführt ' +
    '(z. B. Spargel weiß/grün). Welcher hier gilt, ist nicht belegt — die Menge steht mit 0,00 € drin.',
  no_catalog_item:
    'Nicht bewertet: für diese Kultur gibt es keinen Preisspiegel-Artikel — die Menge steht mit 0,00 € drin.',
  no_price:
    'Nicht bewertet: für diesen Monat und die Monate davor liegt kein Preis vor. ' +
    'Ein Preis aus einem beliebigen Nachbarmonat wäre geraten, deshalb steht die Menge mit 0,00 € drin.',
  no_month:
    'Nicht bewertet: diese Buchung trägt kein verwertbares Datum, lässt sich also keinem Monatspreis ' +
    'zuordnen — die Menge steht mit 0,00 € drin.',
  no_quantity: 'Nicht bewertet: zu dieser Buchung ist keine Menge erfasst, die bewertet werden könnte.',
  unconvertible_unit:
    'Nicht bewertet: die erfasste Mengeneinheit lässt sich nicht in die Preiseinheit umrechnen — ' +
    'die Menge steht mit 0,00 € drin.',
};

const NUM = new Intl.NumberFormat('de-DE');

/**
 * The hover behind ONE Posten — month, item, and what the average was made of; the
 * refusal reason when there is no price.
 *
 * Assembled from what the fact actually carries, clause by clause, so the sentence never
 * claims a count the block did not ship. A missing clause is silence, not a 0: "Ø aus 0
 * Beobachtungen" would read as a defect in the data rather than as a gap in this UI.
 */
export function postenNote(posten: LeistungPostenItem): string {
  if (!posten.valued) return posten.reason ? REFUSAL_NOTE[posten.reason] : UNPRICED_LABEL;
  const parts: string[] = [];
  if (posten.carried_from) {
    // The weaker answer, said out loud. The price is real; it is just not this month's —
    // and the alternative the worker refuses (a silently neighbouring month) is exactly
    // what this sentence exists to distinguish itself from.
    parts.push(`Bewertet mit dem Preis aus ${posten.carried_from} — für ${posten.month} liegt kein eigener Preis vor`);
  } else if (posten.price_month) {
    parts.push(`Bewertet mit dem Monatsdurchschnitt ${posten.price_month}`);
  } else if (posten.basis === 'estimate') {
    // Beyond the carry-forward bound the worker falls through to the curated Buchpreis
    // book. It is a reference price, not an observation, and it has no chart cell to
    // point at — so it says so instead of borrowing the Preisspiegel's authority.
    parts.push('Bewertet mit einem hinterlegten Richtpreis — im Preisspiegel liegt für diesen Zeitraum kein Preis');
  } else {
    parts.push('Bewertet mit einem Preis aus dem Preisspiegel');
  }
  if (posten.catalog_item_id) parts.push(`Artikel: ${posten.catalog_item_id}`);
  if (posten.observation_count !== undefined) {
    const buyers =
      posten.counterparty_count !== undefined ? ` von ${NUM.format(posten.counterparty_count)} Abnehmern` : '';
    parts.push(`Ø aus ${NUM.format(posten.observation_count)} Beobachtungen${buyers}`);
  }
  // The one claim that is always true of this number and is easy to mistake.
  parts.push('gewichtet nach verkaufter Menge über alle Kanäle');
  return `${parts.join(' · ')}.`;
}

/** The drawer heading for the Leistung basis note. */
export const LEISTUNG_BASIS_HEADING = 'Wie die Leistung gerechnet ist';

/**
 * The basis, stated once, where the cascade is. Not a per-row chip: it is true of every
 * Leistung on the page, and a claim that is always true belongs in the explanation rather
 * than on every line of the table.
 */
export const LEISTUNG_BASIS_BODY =
  'Erfasste Menge × gewichteter Monatsdurchschnittspreis aus deinem Preisspiegel — nicht das, ' +
  'was für diese Kultur abgerechnet wurde.';

/**
 * …except for the Posten the Preisspiegel could not reach.
 *
 * Beyond the carry-forward bound the worker falls through to the curated Richtpreis book
 * (`basis: 'estimate'`) — a real price, and NOT the farm's own. Those Posten are `valued`,
 * so they do not trip `leistung_incomplete`, and the cycle carries no `leistung_estimated`
 * marker either (that flag is for a cycle the valuation priced ENTIRELY from the curated
 * book). Without this sentence the basis note above claims every € came from the farm's
 * own Preisspiegel, on a mixed block where some of it demonstrably did not — a false
 * provenance claim, which is the same defect class as pointing a `↗` at a chart cell that
 * did not produce the price. Found by codex on this change.
 */
export const CURATED_PRICE_NOTE =
  'Für einen Teil der Mengen liegt im Preisspiegel kein Preis in Reichweite — sie sind mit einem ' +
  'hinterlegten Richtpreis bewertet, nicht mit deinen eigenen Verkäufen.';

/** How many Posten that was, or null when the whole block came from the Preisspiegel. */
export function curatedPostenCount(block: LeistungPostenBlock): number {
  return block.items.filter((item) => item.valued && item.basis === 'estimate').length;
}

/**
 * D1 / HOF-45 — the VAT caveat, per cycle rather than in a worker log.
 *
 * Deliberately NOT a row-quality chip. It fires on essentially every prod cycle today
 * (nothing writes ``provenance.tax_basis``), and a warning that fires everywhere is one
 * nobody reads — worse, it would flip every row to „untrustworthy" and drown the
 * ``leistung_unpriced`` signal, which is per-cycle and actionable. It rides in the basis
 * note, where the reader is already being told where the € came from.
 */
export const BASIS_UNKNOWN_NOTE =
  'Für einen Teil der Preise ist nicht belegt, ob sie netto erfasst sind — der Betrag kann die ' +
  'Mehrwertsteuer enthalten und damit etwas zu hoch liegen.';

/**
 * D2 — how much of this cycle's harvest went unpriced, or null when all of it was priced.
 *
 * Counts, not a percentage: the reader's next move is to look at WHICH Posten carry the
 * chip in the panel below, and a share would round two Posten and two hundred into the
 * same sentence.
 */
export function unpricedNote(block: LeistungPostenBlock): string | null {
  if (block.refused_count === 0) return null;
  const total = block.valued_count + block.refused_count;
  return (
    `${NUM.format(block.refused_count)} von ${NUM.format(total)} Posten konnten nicht bewertet werden und ` +
    'stehen mit 0,00 € drin — die Leistung ist dadurch zu niedrig, nicht falsch.'
  );
}

/**
 * The quiet pill beside a Menge that carries no price. Deliberately no
 * ``aria-describedby``: the visible word carries the signal and ``title`` carries the
 * explanation, matching every other hint in this app (codex declined the ARIA indirection
 * on the table badge in PR #2752).
 */
export function UnpricedChip({ title }: { title?: string }) {
  return (
    <>
      {' '}
      <span className="deckungsbeitrag-estimate-chip" title={title ?? UNPRICED_LABEL}>
        {UNPRICED_LABEL}
      </span>
    </>
  );
}
